import React, { useState, useEffect } from 'react';
import { adminAPI } from '../services/api';
import {
  Users,
  Search,
  Filter,
  UserCheck,
  UserX,
  Shield,
  Eye,
  Activity,
  AlertCircle,
  Settings,
  Crown
} from 'lucide-react';
import { toast } from 'react-toastify';

const AdminUsers = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0
  });
  const [selectedUser, setSelectedUser] = useState(null);
  const [showUserModal, setShowUserModal] = useState(false);
  const [userUsage, setUserUsage] = useState(null);
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [subscriptionTiers, setSubscriptionTiers] = useState([]);

  const fetchUsers = React.useCallback(async () => {
    try {
      setLoading(true);
      const params = {
        page: pagination.page,
        limit: pagination.limit,
        search: searchTerm || undefined,
        status: statusFilter !== 'all' ? statusFilter : undefined
      };

      const response = await adminAPI.getUsers(params);
      setUsers(response.data.data.users);
      setPagination(prev => ({
        ...prev,
        total: response.data.data.total,
        totalPages: response.data.data.totalPages
      }));
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Failed to fetch users');
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.limit, searchTerm, statusFilter]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleStatusChange = async (userId, newStatus) => {
    try {
      await adminAPI.updateUserStatus(userId, {
        isActive: newStatus === 'active',
        isAdmin: users.find(u => u.id === userId)?.isAdmin || false
      });

      setUsers(users.map(user =>
        user.id === userId
          ? { ...user, isActive: newStatus === 'active' }
          : user
      ));

      toast.success(`User ${newStatus === 'active' ? 'activated' : 'deactivated'} successfully`);
    } catch (error) {
      console.error('Error updating user status:', error);
      toast.error('Failed to update user status');
    }
  };

  const handleAdminToggle = async (userId, isAdmin) => {
    try {
      const user = users.find(u => u.id === userId);
      await adminAPI.updateUserStatus(userId, {
        isActive: user.isActive,
        isAdmin: !isAdmin
      });

      setUsers(users.map(user =>
        user.id === userId
          ? { ...user, isAdmin: !isAdmin }
          : user
      ));

      toast.success(`User ${!isAdmin ? 'promoted to' : 'removed from'} admin successfully`);
    } catch (error) {
      console.error('Error updating admin status:', error);
      toast.error('Failed to update admin status');
    }
  };

  const fetchSubscriptionTiers = async () => {
    try {
      const response = await adminAPI.getSubscriptionTiers();
      setSubscriptionTiers(response.data.data.tiers);
    } catch (error) {
      console.error('Error fetching subscription tiers:', error);
      toast.error('Failed to fetch subscription tiers');
    }
  };

  const handleSubscriptionChange = async (userId, subscriptionTierId) => {
    try {
      await adminAPI.updateUserSubscription(userId, { subscriptionTierId });

      // Refresh users to get updated subscription data
      await fetchUsers();

      setShowSubscriptionModal(false);
      setSelectedUser(null);
      toast.success('User subscription updated successfully');
    } catch (error) {
      console.error('Error updating user subscription:', error);
      toast.error('Failed to update user subscription');
    }
  };

  const openSubscriptionModal = async (user) => {
    setSelectedUser(user);
    setShowSubscriptionModal(true);
    if (subscriptionTiers.length === 0) {
      await fetchSubscriptionTiers();
    }
  };

  const viewUserDetails = async (user) => {
    setSelectedUser(user);
    setShowUserModal(true);
    
    try {
      const response = await adminAPI.getUserUsage(user.id, 30);
      setUserUsage(response.data.data);
    } catch (error) {
      console.error('Error fetching user usage:', error);
      setUserUsage(null);
    }
  };

  const filteredUsers = users.filter(user => {
    const matchesSearch = !searchTerm || 
      user.username?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' ||
      (statusFilter === 'active' && user.isActive) ||
      (statusFilter === 'inactive' && !user.isActive) ||
      (statusFilter === 'admin' && user.isAdmin);
    
    return matchesSearch && matchesStatus;
  });

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString();
  };

  if (loading && users.length === 0) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center">
              <Users className="w-8 h-8 mr-3 text-blue-600" />
              User Management
            </h1>
            <p className="text-gray-600 mt-2">
              Manage user accounts, permissions, and activity
            </p>
          </div>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="card p-6 mb-6">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Search users by email or username..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="input-field pl-10"
            />
          </div>
          
          <div className="flex items-center space-x-2">
            <Filter className="w-4 h-4 text-gray-500" />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="rounded border-gray-300 text-sm focus:border-blue-500 focus:ring-blue-500"
            >
              <option value="all">All Users</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="admin">Admins</option>
            </select>
          </div>
        </div>
      </div>

      {/* Users Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="card p-4">
          <div className="flex items-center">
            <Users className="w-6 h-6 text-blue-600 mr-2" />
            <div>
              <p className="text-sm text-gray-600">Total Users</p>
              <p className="text-xl font-bold">{pagination.total}</p>
            </div>
          </div>
        </div>
        <div className="card p-4">
          <div className="flex items-center">
            <UserCheck className="w-6 h-6 text-green-600 mr-2" />
            <div>
              <p className="text-sm text-gray-600">Active</p>
              <p className="text-xl font-bold">{users.filter(u => u.isActive).length}</p>
            </div>
          </div>
        </div>
        <div className="card p-4">
          <div className="flex items-center">
            <UserX className="w-6 h-6 text-red-600 mr-2" />
            <div>
              <p className="text-sm text-gray-600">Inactive</p>
              <p className="text-xl font-bold">{users.filter(u => !u.isActive).length}</p>
            </div>
          </div>
        </div>
        <div className="card p-4">
          <div className="flex items-center">
            <Shield className="w-6 h-6 text-purple-600 mr-2" />
            <div>
              <p className="text-sm text-gray-600">Admins</p>
              <p className="text-xl font-bold">{users.filter(u => u.isAdmin).length}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Users Table */}
      <div className="card overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium">Users ({filteredUsers.length})</h3>
        </div>
        
        {filteredUsers.length === 0 ? (
          <div className="text-center py-12">
            <Users className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Users Found</h3>
            <p className="text-gray-600">No users match your current filters.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    User
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Role
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Subscription
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    API Keys
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Last Login
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Created
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredUsers.map((user) => (
                  <tr key={user.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10">
                          <div className="h-10 w-10 rounded-full bg-gray-300 flex items-center justify-center">
                            <span className="text-sm font-medium text-gray-700">
                              {(user.username || user.email).charAt(0).toUpperCase()}
                            </span>
                          </div>
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">
                            {user.username || 'N/A'}
                          </div>
                          <div className="text-sm text-gray-500">{user.email}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        user.isActive
                          ? 'bg-green-100 text-green-800'
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {user.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        user.isAdmin
                          ? 'bg-purple-100 text-purple-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        {user.isAdmin ? 'Admin' : 'User'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        user.subscriptionTier === 'free'
                          ? 'bg-gray-100 text-gray-800'
                          : user.subscriptionTier === 'donator'
                          ? 'bg-blue-100 text-blue-800'
                          : user.subscriptionTier === 'premium'
                          ? 'bg-purple-100 text-purple-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        <Crown className="w-3 h-3 mr-1" />
                        {user.subscriptionTier ?
                          user.subscriptionTier.charAt(0).toUpperCase() + user.subscriptionTier.slice(1) : 'Free'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {user.apiKeyCount || 0}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {user.lastLogin ? formatDate(user.lastLogin) : 'Never'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(user.createdAt)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => viewUserDetails(user)}
                          className="btn btn-icon-sm btn-soft-info"
                          title="View Details"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleStatusChange(
                            user.id,
                            user.isActive ? 'inactive' : 'active'
                          )}
                          className={`btn btn-icon-sm ${
                            user.isActive
                              ? 'btn-soft-danger'
                              : 'btn-soft-success'
                          }`}
                          title={user.isActive ? 'Deactivate' : 'Activate'}
                        >
                          {user.isActive ? <UserX className="w-4 h-4" /> : <UserCheck className="w-4 h-4" />}
                        </button>
                        <button
                          onClick={() => handleAdminToggle(user.id, user.isAdmin)}
                          className={`btn btn-icon-sm ${
                            user.isAdmin
                              ? 'btn-soft-warning'
                              : 'btn-soft-secondary'
                          }`}
                          title={user.isAdmin ? 'Remove Admin' : 'Make Admin'}
                        >
                          <Shield className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => openSubscriptionModal(user)}
                          className="btn btn-icon-sm btn-soft-purple"
                          title="Manage Subscription"
                        >
                          <Settings className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
            <div className="text-sm text-gray-700">
              Showing {((pagination.page - 1) * pagination.limit) + 1} to {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} results
            </div>
            <div className="flex space-x-1">
              <button
                onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                disabled={pagination.page === 1}
                className="btn btn-sm btn-secondary"
              >
                Previous
              </button>
              {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                const page = i + 1;
                return (
                  <button
                    key={page}
                    onClick={() => setPagination(prev => ({ ...prev, page }))}
                    className={`btn btn-sm ${
                      pagination.page === page
                        ? 'btn-primary'
                        : 'btn-secondary'
                    }`}
                  >
                    {page}
                  </button>
                );
              })}
              <button
                onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                disabled={pagination.page === pagination.totalPages}
                className="btn btn-sm btn-secondary"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* User Details Modal */}
      {showUserModal && selectedUser && (
        <UserDetailsModal
          user={selectedUser}
          usage={userUsage}
          onClose={() => {
            setShowUserModal(false);
            setSelectedUser(null);
            setUserUsage(null);
          }}
        />
      )}

      {/* Subscription Management Modal */}
      {showSubscriptionModal && selectedUser && (
        <SubscriptionModal
          user={selectedUser}
          subscriptionTiers={subscriptionTiers}
          onClose={() => {
            setShowSubscriptionModal(false);
            setSelectedUser(null);
          }}
          onSave={handleSubscriptionChange}
        />
      )}
    </div>
  );
};

// User Details Modal Component
const UserDetailsModal = ({ user, usage, onClose }) => {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-semibold">User Details</h3>
          <button
            onClick={onClose}
            className="btn btn-icon-sm btn-ghost"
          >
            ×
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* User Info */}
          <div>
            <h4 className="text-lg font-medium mb-4">Account Information</h4>
            <div className="space-y-3">
              <div>
                <label className="text-sm font-medium text-gray-600">Username</label>
                <p className="text-sm">{user.username || 'N/A'}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-600">Email</label>
                <p className="text-sm">{user.email}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-600">Status</label>
                <p>
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    user.isActive
                      ? 'bg-green-100 text-green-800'
                      : 'bg-red-100 text-red-800'
                  }`}>
                    {user.isActive ? 'Active' : 'Inactive'}
                  </span>
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-600">Role</label>
                <p>
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    user.isAdmin
                      ? 'bg-purple-100 text-purple-800'
                      : 'bg-gray-100 text-gray-800'
                  }`}>
                    {user.isAdmin ? 'Admin' : 'User'}
                  </span>
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-600">Created</label>
                <p className="text-sm">{new Date(user.createdAt).toLocaleString()}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-600">Last Login</label>
                <p className="text-sm">
                  {user.lastLogin ? new Date(user.lastLogin).toLocaleString() : 'Never'}
                </p>
              </div>
            </div>
          </div>

          {/* Usage Stats */}
          <div>
            <h4 className="text-lg font-medium mb-4">Usage Statistics (Last 30 Days)</h4>
            {usage ? (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-3 bg-blue-50 rounded">
                    <div className="flex items-center">
                      <Activity className="w-5 h-5 text-blue-600 mr-2" />
                      <div>
                        <p className="text-sm font-medium">Total Requests</p>
                        <p className="text-lg font-bold">{usage.totalRequests || 0}</p>
                      </div>
                    </div>
                  </div>
                  <div className="p-3 bg-green-50 rounded">
                    <div className="flex items-center">
                      <UserCheck className="w-5 h-5 text-green-600 mr-2" />
                      <div>
                        <p className="text-sm font-medium">Success Rate</p>
                        <p className="text-lg font-bold">
                          {usage.totalRequests ? Math.round((usage.successfulRequests / usage.totalRequests) * 100) : 0}%
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="p-3 bg-red-50 rounded">
                    <div className="flex items-center">
                      <AlertCircle className="w-5 h-5 text-red-600 mr-2" />
                      <div>
                        <p className="text-sm font-medium">Errors</p>
                        <p className="text-lg font-bold">{usage.errorCount || 0}</p>
                      </div>
                    </div>
                  </div>
                  <div className="p-3 bg-purple-50 rounded">
                    <div className="flex items-center">
                      <Activity className="w-5 h-5 text-purple-600 mr-2" />
                      <div>
                        <p className="text-sm font-medium">API Keys</p>
                        <p className="text-lg font-bold">{user.api_key_count || 0}</p>
                      </div>
                    </div>
                  </div>
                </div>

                {usage.dailyUsage && usage.dailyUsage.length > 0 && (
                  <div>
                    <h5 className="text-sm font-medium mb-2">Recent Activity</h5>
                    <div className="space-y-2 max-h-40 overflow-y-auto">
                      {usage.dailyUsage.slice(0, 10).map((day, index) => (
                        <div key={index} className="flex justify-between text-xs p-2 bg-gray-50 rounded">
                          <span>{new Date(day.date).toLocaleDateString()}</span>
                          <span>{day.requests} requests</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="flex items-center justify-center h-32">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
              </div>
            )}
          </div>
        </div>

        <div className="mt-6 flex justify-end">
          <button
            onClick={onClose}
            className="btn-secondary"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

// Subscription Management Modal Component
const SubscriptionModal = ({ user, subscriptionTiers, onClose, onSave }) => {
  const [selectedTierId, setSelectedTierId] = useState(user.currentSubscriptionTierId || '');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedTierId) return;

    setLoading(true);
    try {
      await onSave(user.id, parseInt(selectedTierId));
    } catch (error) {
      console.error('Error updating subscription:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-md w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">
            Manage Subscription
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <span className="sr-only">Close</span>
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6">
          <div className="mb-4">
            <h4 className="text-sm font-medium text-gray-900 mb-2">User</h4>
            <div className="flex items-center">
              <div className="flex-shrink-0 h-8 w-8">
                <div className="h-8 w-8 rounded-full bg-gray-300 flex items-center justify-center">
                  <span className="text-xs font-medium text-gray-700">
                    {(user.username || user.email).charAt(0).toUpperCase()}
                  </span>
                </div>
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-900">{user.username || 'N/A'}</p>
                <p className="text-xs text-gray-500">{user.email}</p>
              </div>
            </div>
          </div>

          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Current Subscription: {user.subscriptionTier ? user.subscriptionTier.charAt(0).toUpperCase() + user.subscriptionTier.slice(1) : 'Free'}
            </label>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select New Subscription Tier *
            </label>
            <select
              value={selectedTierId}
              onChange={(e) => setSelectedTierId(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            >
              <option value="">Select a tier...</option>
              {subscriptionTiers.map((tier) => (
                <option key={tier.id} value={tier.id}>
                  {tier.displayName} - {tier.maxApiKeys} keys, {tier.dailyRequestLimit.toLocaleString()}/day
                </option>
              ))}
            </select>
          </div>

          {selectedTierId && (
            <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-md">
              <h5 className="text-sm font-medium text-blue-900 mb-2">Tier Details</h5>
              {(() => {
                const selectedTier = subscriptionTiers.find(t => t.id === parseInt(selectedTierId));
                if (!selectedTier) return null;

                return (
                  <div className="space-y-1 text-xs text-blue-800">
                    <p><strong>Max API Keys:</strong> {selectedTier.maxApiKeys}</p>
                    <p><strong>Daily Limit:</strong> {selectedTier.dailyRequestLimit.toLocaleString()} requests</p>
                    <p><strong>Minute Limit:</strong> {selectedTier.minuteRequestLimit.toLocaleString()} requests</p>
                  </div>
                );
              })()}
            </div>
          )}

          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={onClose}
              className="btn btn-md btn-soft-gray"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-md btn-primary"
              disabled={loading || !selectedTierId}
            >
              {loading ? 'Updating...' : 'Update Subscription'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AdminUsers;